import React from 'react';
import { PropTypes } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Col, Container, Input, Row } from 'reactstrap';
import { isUndefined, mapValues, pick } from 'lodash';
import { PaymentMethod } from '@payer-admin-ui/api/src/invoices/models/PaymentMethod';
import { CurrencyCode } from '@payer-admin-ui/api/src/invoices/models/CurrencyCode';
import { Validators } from '@payer-admin-ui/components-v2/src/inputs/validation';
import { ExpandableContainer } from '../../ExpandableContainer/ExpandableContainer';
import { AmountInput } from '../../Input/AmountInput';
import { TextInput } from '../../Input/TextInput';
import { PayerDropdown } from '../../Dropdowns/PayerDropdown';
import { CheckboxGroup } from '../../Checkbox/CheckboxGroup';
import styles from '../DetailedInvoiceCard/InvoiceCustomerCard.module.scss';

const InvoiceSubscriptionDetailsCard = ({ subscription, patchSubscription, disabled, disabledIndividually }) => {
  const { t } = useTranslation();

  const {
    externalReferenceId,
    periodTime,
    periodTimeUnit,
    startDate,
    endDate,
    nextBillingDate,
    deliveryType,
    subscriptionPaymentMethod,
    templateOrder: {
      additionalReferenceId,
      description,
      comment,
      currencyCode,
      referenceId,
      invoiceCustomer: { address: { yourReference } = {} } = {},
    } = {},
  } = subscription ?? {};

  const { type, dueDays, paymentMethods } = subscription?.invoiceConfig ?? {};

  /**  When creating a subscription a startDate is used but this is not saved.
   * Instead a nextBillingDate is saved and used when we view and update the subscription. */
  const startDateTitle = !isUndefined(nextBillingDate)
    ? t('components.cards.subscription.details.nextBillingDate')
    : t('date.startDate');
  const startDateValue = !isUndefined(nextBillingDate) ? nextBillingDate : startDate;

  return (
    <ExpandableContainer
      header={<b>{t('components.cards.subscription.details.title')}</b>}
      defaultExpanded={true}
      sx={{ mb: 2 }}
    >
      {subscription && (
        <Container className={styles.InvoiceCustomerCard}>
          <Row className={styles.section}>
            <Col xs="12" md="6">
              <h5>{t('components.cards.subscription.details.subscriptionPaymentMethod')}</h5>
              <PayerDropdown
                items={t('enums.subscriptionPaymentMethod.values', { returnObjects: true })}
                selectedItem={subscriptionPaymentMethod}
                noSelectedItemText={t('components.cards.subscription.details.noSubscriptionPaymentMethod')}
                onItemSelect={subscriptionPaymentMethod => patchSubscription({ subscriptionPaymentMethod })}
                disabled={disabledIndividually?.subscriptionPaymentMethod ?? disabled}
              />
            </Col>
          </Row>
          <Row className={styles.section}>
            <Col xs="12" sm="12">
              <TextInput
                title={t('components.cards.subscription.details.externalReferenceId')}
                value={externalReferenceId}
                setValue={externalReferenceId => patchSubscription({ externalReferenceId })}
                validators={[Validators.notEmpty, Validators.length(0, 48)]}
                disabled={disabledIndividually?.externalReferenceId ?? disabled}
              />
            </Col>
          </Row>
          <Row className={styles.section}>
            <Col xs="12" md={subscriptionPaymentMethod === 'INVOICE' ? '3' : '4'}>
              <h5>{startDateTitle}</h5>
              <Input
                type="date"
                id="startDate"
                value={startDateValue ?? ''}
                onChange={event =>
                  patchSubscription({
                    startDate: event.target.value,
                  })
                }
                disabled={disabledIndividually?.startDate ?? disabled}
              />
            </Col>
            <Col xs="12" md={subscriptionPaymentMethod === 'INVOICE' ? '3' : '4'}>
              <h5>{t('date.endDate')}</h5>
              <Input
                type="date"
                id="endDate"
                value={endDate ?? ''}
                onChange={event => patchSubscription({ endDate: event.target.value })}
                disabled={disabledIndividually?.endDate ?? disabled}
              />
            </Col>
            <Col xs="12" md={subscriptionPaymentMethod === 'INVOICE' ? '3' : '4'}>
              <h5>{t('components.cards.subscription.details.periodTime')}</h5>
              <AmountInput
                title=""
                value={periodTime}
                placeholder="0"
                step={1}
                setValue={periodTime => patchSubscription({ periodTime })}
                validators={[Validators.notEmpty, Validators.notZero, Validators.positive]}
                disabled={disabledIndividually?.periodTime ?? disabled}
                unit={t(`enums.periodTimeUnit.values.${periodTimeUnit}`)}
              />
            </Col>
            {subscriptionPaymentMethod === 'INVOICE' && (
              <Col xs="12" md="3">
                <h5>{t('components.cards.subscription.details.dueDays')}</h5>
                <AmountInput
                  title=""
                  value={dueDays}
                  placeholder="0"
                  step={1}
                  setValue={dueDays => patchSubscription({ invoiceConfig: { dueDays } })}
                  validators={[Validators.notZero, Validators.positive]}
                  disabled={disabledIndividually?.dueDays ?? disabled}
                  unit="Day"
                />
              </Col>
            )}
          </Row>
          <Row className={styles.section}>
            <Col xs="12" md={subscriptionPaymentMethod === 'INVOICE' ? '3' : '4'}>
              <h5>{t('components.cards.subscription.details.currencyCode')}</h5>
              <PayerDropdown
                items={CurrencyCode}
                selectedItem={currencyCode}
                noSelectedItemText={t('components.cards.subscription.details.noCurrencyCode')}
                onItemSelect={currencyCode => patchSubscription({ templateOrder: { currencyCode } })}
                disabled={disabledIndividually?.currencyCode ?? disabled}
              />
            </Col>
            {subscriptionPaymentMethod === 'INVOICE' && (
              <Col xs="12" md="3">
                <h5>{t('components.cards.subscription.details.type')}</h5>
                <PayerDropdown
                  items={pick(t('enums.invoiceType.values', { returnObjects: true }), ['NORMAL', 'PREPAYMENT'])}
                  selectedItem={type}
                  onItemSelect={type => patchSubscription({ invoiceConfig: { type } })}
                  disabled={disabledIndividually?.type ?? disabled}
                />
              </Col>
            )}
            <Col xs="12" md={subscriptionPaymentMethod === 'INVOICE' ? '3' : '4'}>
              <h5>{t('components.cards.subscription.details.deliveryType')}</h5>
              <PayerDropdown
                items={t('enums.deliveryType.values', { returnObjects: true })}
                selectedItem={deliveryType}
                onItemSelect={deliveryType => patchSubscription({ deliveryType })}
                disabled={disabledIndividually?.deliveryType ?? disabled}
              />
            </Col>
            <Col xs="12" md={subscriptionPaymentMethod === 'INVOICE' ? '3' : '4'}>
              <h5>{t('enums.periodTimeUnit.name')}</h5>
              <PayerDropdown
                items={t('enums.periodTimeUnit.values', { returnObjects: true })}
                selectedItem={periodTimeUnit}
                onItemSelect={periodTimeUnit => patchSubscription({ periodTimeUnit })}
                disabled={disabledIndividually?.periodTimeUnit ?? disabled}
              />
            </Col>
          </Row>
          <Row className={styles.section}>
            <Col xs="12" sm="12">
              <TextInput
                title={t('components.cards.subscription.details.orderReferenceId')}
                value={referenceId}
                setValue={referenceId => patchSubscription({ templateOrder: { referenceId } })}
                validators={[Validators.notEmpty, Validators.length(0, 48)]}
                disabled={disabledIndividually?.referenceId ?? disabled}
              />
            </Col>
            <Col xs="12" sm="6">
              <TextInput
                title={t('components.cards.subscription.details.additionalReferenceId')}
                value={additionalReferenceId}
                setValue={additionalReferenceId => patchSubscription({ templateOrder: { additionalReferenceId } })}
                validators={[Validators.length(0, 48)]}
                disabled={disabledIndividually?.additionalReferenceId ?? disabled}
              />
            </Col>
            <Col xs="12" sm="6">
              <TextInput
                title={t('components.cards.subscription.details.yourReference')}
                value={yourReference}
                setValue={yourReference =>
                  patchSubscription({ templateOrder: { invoiceCustomer: { address: { yourReference } } } })
                }
                validators={[Validators.length(0, 48)]}
                disabled={disabledIndividually?.yourReference ?? disabled}
              />
            </Col>
          </Row>
          <Row>
            <Col xs="12">
              <TextInput
                title={t('components.cards.subscription.details.description')}
                value={description}
                setValue={description => patchSubscription({ templateOrder: { description } })}
                validators={[Validators.notEmpty, Validators.length(0, 128)]}
                disabled={disabledIndividually?.description ?? disabled}
              />
            </Col>
          </Row>
          <Row>
            <Col xs="12">
              <TextInput
                title={t('components.cards.subscription.details.comment')}
                value={comment}
                setValue={comment => patchSubscription({ templateOrder: { comment } })}
                validators={[Validators.length(0, 128)]}
                disabled={disabledIndividually?.comment ?? disabled}
              />
            </Col>
          </Row>
          {subscriptionPaymentMethod === 'INVOICE' && (
            <Row className={styles.section}>
              <Col xs={12} md={12} lg={12}>
                <CheckboxGroup
                  title={t('components.cards.subscription.details.paymentMethods')}
                  items={mapValues(PaymentMethod, value => t(`enums.paymentMethod.values.${value}`))}
                  selectedItems={paymentMethods}
                  setSelectedItems={paymentMethods => patchSubscription({ invoiceConfig: { paymentMethods } })}
                  disabled={disabledIndividually?.paymentMethods ?? disabled}
                />
              </Col>
            </Row>
          )}
        </Container>
      )}
    </ExpandableContainer>
  );
};

InvoiceSubscriptionDetailsCard.propTypes = {
  subscription: PropTypes.shape({
    referenceId: PropTypes.string,
    yourReference: PropTypes.string,
    ourReference: PropTypes.string,
    description: PropTypes.string,
    comment: PropTypes.string,
    currencyCode: PropTypes.string,
    invoiceConfig: PropTypes.object,
  }),
  patchSubscription: PropTypes.func,
  disabled: PropTypes.bool,
  disabledIndividually: PropTypes.object,
};

export { InvoiceSubscriptionDetailsCard };
